import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { OrphanAssetScan, OrphanAssetDeleteResult } from "../types";

const ORPHAN_FIELDS = `
  key
  url
  fileName
  size
  lastModified
  organizationId
  organizationName
  entityType
  entityId
  reason
  reasonLabel
  reasonDetail
  deletable
  supersededBy
`;

export const orphanAssetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrphanAssets: builder.query<OrphanAssetScan, string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query OrphanAssets($organizationId: String) {
              orphanAssets(organizationId: $organizationId) {
                summary {
                  bucketPrefix
                  totalObjects
                  totalBytes
                  referencedObjects
                  orphanCount
                  orphanBytes
                  deletableCount
                  deletableBytes
                  protectedCount
                }
                orphans {
                  ${ORPHAN_FIELDS}
                }
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<OrphanAssetScan>(res, "orphanAssets"),

      providesTags: [{ type: "OrphanAsset" as const, id: "LIST" }],
    }),

    deleteOrphanAssets: builder.mutation<OrphanAssetDeleteResult, string[]>({
      query: (keys) => ({
        body: {
          query: `
            mutation DeleteOrphanAssets($keys: [String!]!) {
              deleteOrphanAssets(keys: $keys) {
                deletedCount
                deletedBytes
                errors { key reason }
              }
            }
          `,
          variables: { keys },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<OrphanAssetDeleteResult>(res, "deleteOrphanAssets"),

      invalidatesTags: [{ type: "OrphanAsset", id: "LIST" }],
    }),

    adoptOrphanAsset: builder.mutation<boolean, string>({
      query: (key) => ({
        body: {
          query: `
            mutation AdoptOrphanAsset($key: String!) {
              adoptOrphanAsset(key: $key)
            }
          `,
          variables: { key },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "adoptOrphanAsset"),

      // Adopting gives the file an `assets` row, so it leaves this list AND
      // joins the Gallery — both caches have to drop.
      invalidatesTags: [
        { type: "OrphanAsset", id: "LIST" },
        { type: "Asset", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetOrphanAssetsQuery,
  useDeleteOrphanAssetsMutation,
  useAdoptOrphanAssetMutation,
} = orphanAssetApi;
